import React, { createContext, useCallback, useContext, useMemo } from 'react'

import { SEARCH_TRACK_TAGS_FILTER_FIELD } from '@disco/constants/search'
import {
  selectSearchPage,
  setFilters,
} from '@disco/state-management/reducers/search-page'

import type { FilterState } from 'types/filter-page'

import { useDispatch, useSelector } from 'hooks/use-redux'

type IndicatorFilterContextValue = {
  activeTagIds: number[]
  filterByTag: (tagId: number) => void
  clearTagFilter: () => void
}

const IndicatorFilterContext = createContext<IndicatorFilterContextValue>({
  activeTagIds: [],
  filterByTag: () => {},
  clearTagFilter: () => {},
})

export const useIndicatorFilter = (): IndicatorFilterContextValue =>
  useContext(IndicatorFilterContext)

const tagsFilterDefinition = {
  label: 'Tags',
  type: 'tags' as const,
  field: SEARCH_TRACK_TAGS_FILTER_FIELD,
}

/**
 * Makes the colour indicators in the search table clickable: each one toggles
 * its tag in and out of the search's tag filter.
 */
export const IndicatorFilterProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactNode => {
  const dispatch = useDispatch()
  const { filters } = useSelector(selectSearchPage)

  // Matched on field alone: a freshly added, still value-less tags filter must
  // be reused too, or a dot click would append a second track_tags filter.
  const tagsFilter = useMemo(
    () =>
      filters.find(
        filter => filter.definition.field === SEARCH_TRACK_TAGS_FILTER_FIELD,
      ),
    [filters],
  )

  const activeTagIds = useMemo<number[]>(
    () => (Array.isArray(tagsFilter?.value) ? tagsFilter.value : []),
    [tagsFilter],
  )

  const clearTagFilter = useCallback(() => {
    if (!tagsFilter) return
    dispatch(setFilters(filters.filter(filter => filter !== tagsFilter)))
  }, [dispatch, filters, tagsFilter])

  const filterByTag = useCallback(
    (tagId: number) => {
      const nextTagIds = activeTagIds.includes(tagId)
        ? activeTagIds.filter(id => id !== tagId)
        : [...activeTagIds, tagId]

      if (nextTagIds.length === 0) {
        clearTagFilter()
        return
      }

      // Reuse the existing filter object so its id (and therefore its badge in
      // the filter bar) survives the update, and so an operator the user picked
      // in the filter bar is not silently reset to "equals".
      const nextFilter: FilterState = {
        ...tagsFilter,
        definition: tagsFilter?.definition ?? tagsFilterDefinition,
        value: nextTagIds,
        operator: tagsFilter?.operator ?? 'equals',
      }

      dispatch(
        setFilters(
          tagsFilter
            ? filters.map(filter =>
                filter === tagsFilter ? nextFilter : filter,
              )
            : [...filters, nextFilter],
        ),
      )
    },
    [activeTagIds, clearTagFilter, dispatch, filters, tagsFilter],
  )

  const value = useMemo(
    () => ({ activeTagIds, filterByTag, clearTagFilter }),
    [activeTagIds, clearTagFilter, filterByTag],
  )

  return (
    <IndicatorFilterContext.Provider value={value}>
      {children}
    </IndicatorFilterContext.Provider>
  )
}
